/**
 * EnablePushButton — a small banner shown when the user hasn't enabled web
 * push yet. Auto-hides when push is unsupported, already granted, or
 * permission is denied (the user has to re-enable from browser settings).
 *
 * Usage:
 *   <EnablePushButton compact /> // inline pill style for toolbars
 *   <EnablePushButton />         // full-width banner style for dashboard top
 */
import React, { useEffect, useState } from "react";
import { Bell, BellOff, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { enableWebPush, disableWebPush, fetchPushStatus, pushSupported, pushPermissionState, sendPushTest } from "@/lib/push";

export default function EnablePushButton({ compact = false }) {
  const [perm, setPerm] = useState("default");
  const [subs, setSubs] = useState(0);
  const [busy, setBusy] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    setSupported(pushSupported());
    setPerm(pushPermissionState());
    (async () => {
      const s = await fetchPushStatus();
      setConfigured(!!s.configured);
      setSubs(s.subscriptions || 0);
    })();
  }, []);

  const granted = perm === "granted" && subs > 0;

  if (!supported) return null;
  if (!configured) return null; // server hasn't configured VAPID — silently hide
  if (perm === "denied") {
    return compact ? null : (
      <div className="rounded-2xl bg-[#FEE2E2] border border-red-200 p-3 text-sm text-[#991B1B] flex items-center gap-2" data-testid="push-denied-banner">
        <BellOff className="w-4 h-4" />
        Browser notifications are blocked. Re-enable them in your browser site settings to receive alerts.
      </div>
    );
  }

  async function enable() {
    setBusy(true);
    try {
      await enableWebPush();
      setPerm("granted");
      setSubs((n) => n + 1);
      toast.success("Push notifications enabled on this device");
    } catch (e) {
      toast.error(e?.message || "Could not enable notifications");
    } finally { setBusy(false); }
  }

  async function disable() {
    setBusy(true);
    try {
      await disableWebPush();
      setSubs(0);
      toast.success("Notifications disabled on this device");
    } catch (e) {
      toast.error(e?.message || "Could not disable notifications");
    } finally { setBusy(false); }
  }

  async function sendTest() {
    setBusy(true);
    try {
      const r = await sendPushTest();
      toast.success(`Test push sent (${r?.sent || 0})`);
    } catch (e) {
      toast.error(e?.message || "Could not send test");
    } finally { setBusy(false); }
  }

  if (compact) {
    return granted ? (
      <Button
        variant="outline"
        size="sm"
        onClick={sendTest}
        disabled={busy}
        className="rounded-full text-xs"
        data-testid="push-test-btn"
      >
        <Check className="w-3 h-3 mr-1 text-emerald-600" /> Push on
      </Button>
    ) : (
      <Button
        size="sm"
        onClick={enable}
        disabled={busy}
        className="rounded-full text-xs bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] text-white"
        data-testid="push-enable-btn"
      >
        <Bell className="w-3 h-3 mr-1" /> Enable push
      </Button>
    );
  }

  // Full banner
  if (granted) {
    return (
      <div className="rounded-2xl bg-[#DCFCE7] border border-emerald-200 p-3 text-sm text-[#166534] flex items-center justify-between gap-2" data-testid="push-on-banner">
        <span className="inline-flex items-center gap-2"><Check className="w-4 h-4" /> Push notifications are on for this device.</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={sendTest} disabled={busy} data-testid="push-send-test-btn">Send test</Button>
          <Button variant="ghost" size="sm" onClick={disable} disabled={busy} className="text-red-600 hover:bg-red-50" data-testid="push-disable-btn">Disable</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-[#EFF6FF] border border-[#2563EB]/15 p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid="push-enable-banner">
      <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center shadow-sm shrink-0">
        <Bell className="w-5 h-5 text-[#1D4ED8]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-semibold text-foreground">Get notified instantly</div>
        <p className="text-xs text-slate-600 mt-0.5">
          Turn on push so you see tuition alerts, document expiries, and new inquiries even when SchoolBoss is closed.
        </p>
      </div>
      <Button
        onClick={enable}
        disabled={busy}
        className="rounded-full px-5 bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] text-white"
        data-testid="push-enable-btn"
      >
        <Bell className="w-4 h-4 mr-1.5" /> {busy ? "Enabling…" : "Enable"}
      </Button>
    </div>
  );
}
